import axios from 'axios'
import qs from 'query-string'
import nanoid from 'nanoid'
import Auth from './api/auth'
import Cart from './api/cart'
import Qure from './api/qure'
import Profile from './api/profile'
import Translations from './api/translations'
import S3 from './api/s3'
import Captcha from './api/captcha'
import { canUseDOM, newRelicReport } from '@common/utils'
import { goTo, Routes } from '@common/routes'
import RequestError from '@common/request-error'
import { constants } from '@common/services'

// this function is declared outside the async/await because of a bug on regenerators which are unable to implement dead code elimination when paired with webpack
// https://github.com/babel/babel/issues/7665
function getMockDataIfNecessary(mock, url) {
  let resultData = null
  const isLocalhost = canUseDOM && window.location.hostname === 'localhost'
  const urlParms = canUseDOM ? qs.parse(window.location.search) : {}
  const isSSRMock =
    typeof process !== 'undefined' && process.env.MOCK === 'true'
  if (mock && ((isLocalhost && urlParms.mock === 'true') || isSSRMock)) {
    console.log('Get mock for: ', url, '\nFile: ', mock)
    const defaultRes = { error: 'NO MOCK DATA AVAILABLE' }
    const mockedResponse = require(`../mocks/${mock}`)
    resultData = mockedResponse || defaultRes
  }
  return resultData
}

class ApiManager {
  defaultHeaders = {
    'Content-Type': 'application/json'
  }

  errorCodeTranslationMap = {
    qs_01: 'tk_queryParameterTokenMissing'
  }

  constructor() {
    this.auth = new Auth(this)
    this.cart = new Cart(this)
    this.qure = new Qure(this)
    this.profile = new Profile(this)
    this.S3 = new S3(this)
    this.captcha = new Captcha(this)
    this.translations = new Translations(this)
  }

  getHTTPHeaders = headers => {
    const isBackend =
      typeof process !== 'undefined' && process.env.BACKEND_CALLS === 'true'
    const defaultHeaders = {
      'X-Request-ID': nanoid(),
      'application-id': isBackend
        ? constants.APPLICATION_ID_BACKEND
        : constants.APPLICATION_ID,
      'application-key': isBackend
        ? constants.APPLICATION_KEY_BACKEND
        : constants.APPLICATION_KEY
    }
    const result = {
      ...defaultHeaders,
      ...headers
    }
    // filter out null or falsy headers
    return Object.keys(result).reduce((acc, val) => {
      if (result[val]) {
        acc[val] = result[val]
      }
      return acc
    }, {})
  }

  makeRequest = async ({
    url,
    method = 'GET',
    headers = {},
    params = {},
    data = null,
    mock = null,
    timeout = 0,
    withCredentials = false,
    cancelToken,
    useProxy
  }) => {
    const mockData = getMockDataIfNecessary(mock, url)
    if (mockData) {
      await new Promise(resolve => setTimeout(resolve, 1000)) // Add timeout in case of mock call
      return mockData
    }

    try {
      var opts = {
        method,
        cancelToken,
        headers: this.getHTTPHeaders(headers),
        url:
          useProxy &&
          typeof process !== 'undefined' &&
          process.env.NODE_ENV !== 'production'
            ? `https://localhost:3001/proxy${url}`
            : url.substring(4, 0) === 'http'
            ? url
            : `${constants.BASE_URL}${url}`,
        withCredentials,
        params,
        timeout
      }
      if (data) {
        opts.data = data
      }
      const res = await axios(opts)
      return res.data
    } catch (err) {
      if (axios.isCancel(err)) {
        return null
      }
      newRelicReport(err)
      if (err.response) {
        const data = err.response.data.error
          ? err.response.data.error
          : err.response.data
        this.handleErrorCodeTranslation(data)
        if (err.response.data.error && err.response.data.data) {
          data.data = err.response.data.data
        }
        const redirect = err.response.data.data
          ? err.response.data.data.headless_redirection_url
          : null

        switch (err.response.status) {
          case 401:
            if (canUseDOM) {
              if (
                url.indexOf('/GetCart') !== 0 &&
                url.indexOf('/authentication/login') !== 0 &&
                url.indexOf('/PreOrder') !== 0 &&
                url.indexOf('/account/password/verification') !== 0
              ) {
                goTo(Routes.userLogin, false, true)
              }
            }
            throw new RequestError(
              'Unauthorized',
              err.response.status,
              data,
              opts
            )
          case 404:
            throw new RequestError('Not Found', err.response.status, data, opts)
          case 405:
            if (canUseDOM && redirect) {
              goTo(redirect)
              // we do not throw as we redirect elsewhere
              return
            }
            throw new RequestError(
              'Method Not Allowed',
              err.response.status,
              null,
              opts
            )
          case 400:
            throw new RequestError(
              'Bad request',
              err.response.status,
              data,
              opts
            )
          default:
            throw new RequestError(
              'Internal Server Error',
              err.response.status,
              data,
              opts
            )
        }
      } else if (err.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        throw new RequestError('Gateway timeout', 504, err.request, opts)
      } else {
        // Something happened in setting up the request that triggered an Error
        throw new RequestError('Axios error', 400, err.message, opts)
      }
    }
  }

  get = async (url, opts = {}) => {
    return await this.makeRequest({
      ...opts,
      url,
      method: 'GET'
    })
  }

  post = async (url, opts = {}) => {
    return await this.makeRequest({
      ...opts,
      url,
      method: 'POST'
    })
  }

  put = async (url, opts = {}) => {
    return await this.makeRequest({
      ...opts,
      url,
      method: 'PUT'
    })
  }

  delete = async (url, opts = {}) => {
    return await this.makeRequest({
      ...opts,
      url,
      method: 'DELETE'
    })
  }

  setErrorTranslations = translations => {
    this.errorTranslations = translations
  }

  handleErrorCodeTranslation = error => {
    if (error && error[0] && error[0].code) {
      const translationKey = this.errorCodeTranslationMap[error[0].code]
      const message =
        translationKey &&
        this.errorTranslations &&
        this.errorTranslations[translationKey]
      error[0].message = message || error[0].message
    }
  }
}

export default new ApiManager()
